import { Link, useParams } from 'react-router-dom';
import { newsList } from '@/mocks/news';
import Breadcrumb from '@/components/feature/Breadcrumb';
import useSeo from '@/hooks/useSeo';

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const article = newsList.find((n) => n.id === id);

  useSeo({
    title: article ? `${article.title} - 吉泽明步影视动态` : '动态详情 - 吉泽明步影视动态',
    description: article ? article.summary : '吉泽明步影视动态详情。',
    keywords: article
      ? `吉泽明步,${article.category},影视动态,${article.title}`
      : '吉泽明步,影视动态',
  });

  if (!article) {
    return (
      <div className="container-page flex flex-col items-center justify-center py-24 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
          <i className="ri-article-line text-3xl" aria-hidden="true"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-semibold text-foreground-950">未找到该动态</h1>
        <p className="mt-2 text-sm text-foreground-600">该条动态可能已下线或链接有误。</p>
        <Link
          to="/news"
          className="mt-6 inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-5 py-2.5 text-sm text-background-50 whitespace-nowrap transition-colors hover:bg-primary-700 cursor-pointer"
        >
          <i className="ri-arrow-left-line" aria-hidden="true"></i>
          返回动态列表
        </Link>
      </div>
    );
  }

  const related = newsList.filter((n) => n.id !== article.id).slice(0, 4);

  return (
    <div className="w-full">
      <div className="border-b border-background-200/70 bg-background-100/60">
        <div className="container-page py-6 md:py-8">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '动态', to: '/news' },
              { label: '正文' },
            ]}
          />
        </div>
      </div>

      <article className="container-page py-8 md:py-12">
        <div className="mx-auto max-w-3xl">
          <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
            {article.category}
          </span>
          <h1 className="mt-4 font-heading text-2xl font-semibold leading-snug text-foreground-950 md:text-4xl">
            {article.title}
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-4 border-b border-background-200/70 pb-5 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" aria-hidden="true"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line" aria-hidden="true"></i>
              {article.author}
            </span>
          </div>

          <div className="mt-6 overflow-hidden rounded-lg" style={{ aspectRatio: '16 / 9' }}>
            <img
              src={article.cover}
              alt={article.title}
              title={`${article.title} - 吉泽明步影视动态`}
              className="h-full w-full object-cover"
            />
          </div>

          <div className="mt-8 space-y-5 text-base leading-loose text-foreground-700">
            {article.content.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-background-200/70 pt-6">
            <Link
              to="/news"
              className="inline-flex items-center gap-1.5 rounded-md border border-background-300/70 px-5 py-2.5 text-sm text-foreground-800 whitespace-nowrap transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
            >
              <i className="ri-arrow-left-line" aria-hidden="true"></i>
              返回动态列表
            </Link>
            <Link
              to="/works"
              className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-5 py-2.5 text-sm text-background-50 whitespace-nowrap transition-colors hover:bg-primary-700 cursor-pointer"
            >
              浏览相关作品
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>

        <section className="mx-auto mt-14 max-w-3xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">相关文章推荐</h2>
          <ul className="mt-5 flex flex-col gap-3">
            {related.map((item) => (
              <li key={item.id}>
                <Link
                  to={`/news/${item.id}`}
                  className="group flex items-center justify-between gap-4 rounded-lg border border-background-200/70 bg-background-50 px-5 py-4 transition-colors hover:border-primary-300 cursor-pointer"
                >
                  <div className="min-w-0">
                    <h3 className="truncate font-medium text-foreground-900 group-hover:text-primary-700 transition-colors">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-500">{item.date} · {item.category}</p>
                  </div>
                  <i className="ri-arrow-right-s-line shrink-0 text-foreground-400 group-hover:text-primary-600 transition-colors" aria-hidden="true"></i>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </article>
    </div>
  );
}